import { useTranslation } from "react-i18next";
import { AllBillingEntityDto } from "@/types/api/api-types";
import { useState } from "react";
import { useCancelMandate, useRetrieveBillingRequestFlow } from "@/api/goCardless";
import { toast } from "react-toastify";
import { usePatchBillingEntity } from "@/api/billingEntity.api";
import { PaymentMethodEnum } from "@/constants/enums/paymentMethod.enum";
import { Modal } from "@/components/ui/customs/modal";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { SelectV2 } from "@/components/ui/customs/select-v2";
import { usePaymentMethodOptions } from "@/hooks/useEnumOptions";

type PaymentMethodeEditModalProps = {
  closeModal: () => void;
  billingEntity: AllBillingEntityDto;
};

export const PaymentMethodeEditModal = ({
  closeModal,
  billingEntity,
}: PaymentMethodeEditModalProps) => {
  const { t } = useTranslation(["boClients", "common"]);
  const paymentMethodOptions = usePaymentMethodOptions();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>(
    billingEntity.paymentMethod,
  );
  const [isLoading, setIsLoading] = useState(false);

  const { mutateAsync: cancelMandate } = useCancelMandate();
  const { mutateAsync: updateBillingEntity } = usePatchBillingEntity();
  const { mutateAsync: retrieveBillingRequestFlow } = useRetrieveBillingRequestFlow();

  const handleUpdate = async () => {
    if (billingEntity.paymentMethod === selectedPaymentMethod) {
      closeModal();
      return;
    }
    setIsLoading(true);
    try {
      if (billingEntity.paymentMethod === "PRELEVEMENT") {
        try {
          const ret = await cancelMandate(billingEntity.id);
          if (ret.status !== 200 && ret.status !== 201) {
            toast.error(t("profilePaymentMethodEditModal.paymentProviderError"));
            closeModal();
            return;
          }
        } catch (error) {
          toast.error(t("profilePaymentMethodEditModal.paymentProviderError"));
          closeModal();
          return;
        }
      }
      if (selectedPaymentMethod === "PRELEVEMENT") {
        const response = await retrieveBillingRequestFlow(billingEntity.id);
        window.location.href = response.data.authorisationUrl;
        return;
      }
      await updateBillingEntity({
        ...billingEntity,
        paymentMethod: selectedPaymentMethod as PaymentMethodEnum,
      });
      closeModal();
    } catch {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={true}
      onClose={closeModal}
      title={t("profilePaymentMethodEditModal.title")}
      size="md"
      dismissible={!isLoading}
      footer={
        <div className="flex w-full gap-2">
          <Button type="button" onClick={handleUpdate} disabled={isLoading} className="flex-1">
            {isLoading ? (
              <Loader2 className="animate-spin" />
            ) : (
              t("profilePaymentMethodEditModal.submit")
            )}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={closeModal}
            disabled={isLoading}
            className="flex-1"
          >
            {t("common:actions.cancel")}
          </Button>
        </div>
      }
    >
      <SelectV2
        placeholder={t("profilePaymentMethodEditModal.paymentMethodPlaceholder")}
        options={paymentMethodOptions}
        onChange={(value) => setSelectedPaymentMethod(value)}
        value={selectedPaymentMethod}
        searchable={false}
        clearable={false}
      />
    </Modal>
  );
};
